import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  projectV2ProjectViewModeAtom,
  projectsWithStatsAtom,
  triggerCreateProjectAtom,
  projectsGroupedByCompletionAtom,
  projectV2ShowCompletedProjectsAtom
} from '../../atoms/v3/projectV2Atoms';
import { isDarkAtom } from '../../atoms/shared/uiAtoms';
import { layoutModeAtom } from '../../atoms/v3/uiAtoms';
import { voiceModeAtom } from '../../atoms/v3/chatInputAtoms';
import { useGlobalShortcutActions } from '../../hooks/useGlobalShortcutActions';
import { updateTaskAtom, fetchTasksAtom } from '../../atoms/v0/taskAtoms';
import { fetchProjectsAtom, createProjectAtom, updateProjectAtom } from '../../atoms/v0/projectAtoms';
import { ProjectV2Header } from './components/projects/ProjectV2Header';
import { ProjectItemV2 } from './components/projects/ProjectItemV2';
import { ProjectCardV2 } from './components/projects/ProjectCardV2';
import { ProjectTabsView } from './components/projects/ProjectTabsView';

import { QuickAddBarV2 } from './components/projects/QuickAddBarV2';
import { TaskItemV2List } from './components/tasks/TaskItemV2List';
import { TaskDetailSheet } from './components/tasks/TaskDetailSheet';
import { ChatInputContainer } from './components/chatInput/ChatInputContainer';
import { ProjectEditModal } from './components/projects/ProjectEditModal';
import { FolderDivider } from './components/folders/FolderDivider';
import { DraggableTaskButton } from './components/tasks/DraggableTaskButton';
import { TaskDndContext, UpdateTaskParams } from './components/tasks/TaskDndContext';
import { DroppableArea } from './components/tasks/DroppableArea';
import { foldersAtom, fetchFoldersAtom } from '../../atoms/v3/folderAtoms';
import { projectV2ExpandedFolderIdsAtom } from '../../atoms/v3/projectV2Atoms';
import { groupProjectsByFolder, shouldShowFolderGroup } from '../../utils/folderUtils';

/**
 * ProjectV2 主页面
 * 包含项目和重要性两个标签页，支持多种视图模式
 */
export const ProjectV2: React.FC = () => {
  const { t } = useTranslation(['v3/projects', 'v3/tasks']);
  const navigate = useNavigate();
  const [isDark] = useAtom(isDarkAtom);

  // 状态管理
  const [projectViewMode] = useAtom(projectV2ProjectViewModeAtom);
  const [projectsWithStats] = useAtom(projectsWithStatsAtom);
  const [projectsGrouped] = useAtom(projectsGroupedByCompletionAtom);
  const [showCompletedProjects, setShowCompletedProjects] = useAtom(projectV2ShowCompletedProjectsAtom);
  const [, updateTask] = useAtom(updateTaskAtom);

  // 文件夹相关状态
  const [folders] = useAtom(foldersAtom);
  const [, fetchFolders] = useAtom(fetchFoldersAtom);
  const [expandedFolderIds] = useAtom(projectV2ExpandedFolderIdsAtom);

  // 布局和数据获取
  const layoutMode = useAtomValue(layoutModeAtom);
  const [, fetchTasks] = useAtom(fetchTasksAtom);
  const [, fetchProjects] = useAtom(fetchProjectsAtom);
  const [, createProject] = useAtom(createProjectAtom);
  const [, updateProject] = useAtom(updateProjectAtom);
  const setVoiceMode = useSetAtom(voiceModeAtom);
  const { focusChatInput } = useGlobalShortcutActions();

  // 任务详情弹窗状态
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false);

  // 项目编辑模态框状态
  const [editingProject, setEditingProject] = useState<{
    id: string;
    title: string;
    description: string;
    folderId?: string | null;
  } | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // QuickAdd 状态管理
  const [activeQuickAddProjectId, setActiveQuickAddProjectId] = useState<string | null>(null);

  // 监听全局创建项目触发器
  const [triggerCreateProject, setTriggerCreateProject] = useAtom(triggerCreateProjectAtom);

  // 初始化时获取数据
  useEffect(() => {
    fetchTasks();
    fetchProjects();
    fetchFolders();
  }, [fetchTasks, fetchProjects, fetchFolders]);

  // 导航到项目详情
  const navigateToProjectDetail = useCallback((projectId: string) => {
    navigate(`/v3/projects/${projectId}`);
  }, [navigate]);

  // 滚动到文件夹
  const scrollToFolder = useCallback((folderId: string) => {
    const element = document.getElementById(`folder-${folderId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // 处理任务点击
  const handleTaskClick = useCallback((taskId: string) => {
    setSelectedTaskId(taskId);
    setIsTaskDetailOpen(true);
  }, []);

  // 处理关闭任务详情
  const handleCloseTaskDetail = useCallback(() => {
    setIsTaskDetailOpen(false);
    // 延迟清除任务ID，确保关闭动画完成后再清除
    setTimeout(() => {
      setSelectedTaskId(null);
    }, 300);
  }, []);

  // 处理任务状态切换
  const handleTaskToggle = useCallback(async (taskId: string, completed: boolean) => {
    try {
      await updateTask({
        id: taskId,
        done: completed,
      });
    } catch (error) {
      console.error('ProjectV2: 任务状态切换失败:', error);
    }
  }, [updateTask]);

  // 处理创建新任务
  const handleCreateTask = useCallback(() => {
    setSelectedTaskId(null);
    setIsTaskDetailOpen(true);
  }, []);

  // 处理创建新项目
  const handleCreateProject = useCallback(() => {
    setEditingProject({
      id: '',
      title: '',
      description: '',
      folderId: null
    });
    setIsEditModalOpen(true);
  }, []);

  // 监听全局创建项目触发器
  useEffect(() => {
    if (triggerCreateProject > 0) {
      handleCreateProject();
      // 重置触发器状态，避免重复触发
      setTriggerCreateProject(0);
    }
  }, [triggerCreateProject, handleCreateProject, setTriggerCreateProject]);

  // 处理保存项目
  const handleSaveProject = useCallback(async (title: string, description: string, folderId?: string | null): Promise<boolean> => {
    try {
      console.log('ProjectV2: handleSaveProject 收到参数:', { title, description, folderId });
      if (editingProject?.id) {
        // 更新现有项目
        console.log('ProjectV2: 更新项目，数据:', { id: editingProject.id, title, context: description, folderId });
        const success = await updateProject({
          id: editingProject.id,
          title,
          context: description,
          folderId
        });
        return !!success;
      } else {
        // 创建新项目
        console.log('ProjectV2: 创建项目，数据:', { title, context: description, folderId });
        const success = await createProject({
          title,
          context: description,
          folderId
        });
        return !!success;
      }
    } catch (error) {
      console.error('ProjectV2: 保存项目失败:', error);
      return false;
    }
  }, [editingProject, createProject, updateProject]);

  // 关闭编辑模态框
  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setEditingProject(null);
  }, []);

  // 处理导航到聊天页面并聚焦输入框
  const handleNavigateToChatAndFocus = useCallback(() => {
    navigate('/v3/chat');
    focusChatInput();
  }, [navigate, focusChatInput]);

  // 处理导航到聊天页面并触发文件上传
  const handleNavigateToChatAndUpload = useCallback(() => {
    navigate('/v3/chat');
    setTimeout(() => {
      const uploadButton = document.querySelector('button[aria-label="添加附件"]') as HTMLButtonElement;
      if (uploadButton) {
        uploadButton.click();
      }
    }, 200);
  }, [navigate]);

  // 处理导航到聊天页面并切换到语音模式
  const handleNavigateToChatAndVoice = useCallback(() => {
    navigate('/v3/chat');
    setTimeout(() => {
      setVoiceMode(true);
    }, 100);
  }, [navigate, setVoiceMode]);

  // 处理拖拽更新任务
  const handleTaskUpdate = useCallback(async (taskId: string, updates: UpdateTaskParams) => {
    try {
      const updateData: any = { id: taskId };

      if (updates.projectId !== undefined) {
        updateData.projectId = updates.projectId;
      }
      if (updates.importance !== undefined) {
        updateData.vital = updates.importance.vital;
        updateData.urgent = updates.importance.urgent;
      }

      await updateTask(updateData);
    } catch (error) {
      console.error('ProjectV2: 拖拽更新任务失败:', error);
    }
  }, [updateTask]);

  // 处理快速添加按钮点击
  const handleQuickAddClick = useCallback((projectId: string) => {
    setActiveQuickAddProjectId(activeQuickAddProjectId === projectId ? null : projectId);
  }, [activeQuickAddProjectId]);

  // 处理快速添加关闭
  const handleQuickAddClose = useCallback(() => {
    setActiveQuickAddProjectId(null);
  }, []);

  // 处理任务创建成功
  const handleTaskCreated = useCallback(() => {
    // 刷新任务数据
    fetchTasks();
    // 关闭快速添加界面
    setActiveQuickAddProjectId(null);
  }, [fetchTasks]);

  // 渲染紧凑模式（项目列表）
  const renderCompactView = useMemo(() => {
    // 按文件夹分组项目
    const projectGroups = groupProjectsByFolder(projectsWithStats, folders);

    return (
      <div className="space-y-4">
        {projectGroups.map((group) => {
          const folderId = group.folder?.id || 'ungrouped';
          const isExpanded = group.folder
            ? (expandedFolderIds || []).includes(group.folder.id)
            : (expandedFolderIds || ['ungrouped']).includes('ungrouped');

          // 检查是否应该显示该文件夹组
          if (!shouldShowFolderGroup(group, showCompletedProjects)) {
            return null;
          }

          // 分离未完成和已完成项目
          const incompleteProjects = group.projects.filter(project => project.stats.completionRate < 100);
          const completedProjects = group.projects.filter(project => project.stats.completionRate >= 100);

          return (
            <div key={folderId} className="space-y-2">
              {/* 文件夹分割线 */}
              <FolderDivider
                folder={group.folder}
                projectCount={showCompletedProjects ? group.projects.length : group.incompleteCount}
                isExpanded={isExpanded}
                onFolderClick={scrollToFolder}
              />

              {/* 文件夹内的项目列表 */}
              {isExpanded && (
                <div className="divide-y divide-gray-200 dark:divide-ios-dark-gray4">
                  {/* 未完成项目 */}
                  {incompleteProjects.map((project) => (
                    <ProjectItemV2
                      key={project.id}
                      project={project}
                      onClick={navigateToProjectDetail}
                      showStats={true}
                    />
                  ))}

                  {/* 已完成项目列表 */}
                  {showCompletedProjects && completedProjects.map((project) => (
                    <ProjectItemV2
                      key={project.id}
                      project={project}
                      onClick={navigateToProjectDetail}
                      showStats={true}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }, [projectsWithStats, folders, expandedFolderIds, showCompletedProjects, setShowCompletedProjects, navigateToProjectDetail, shouldShowFolderGroup]);

  // 渲染展开模式（项目卡片 + 任务列表）
  const renderExpandedView = useMemo(() => {
    // 按文件夹分组项目
    const projectGroups = groupProjectsByFolder(projectsWithStats, folders);

    return (
      <div className="space-y-4">
        {projectGroups.map((group) => {
          const folderId = group.folder?.id || 'ungrouped';
          const isExpanded = group.folder
            ? (expandedFolderIds || []).includes(group.folder.id)
            : (expandedFolderIds || ['ungrouped']).includes('ungrouped'); // 未分组也使用展开状态管理

          // 检查是否应该显示该文件夹组
          if (!shouldShowFolderGroup(group, showCompletedProjects)) {
            return null;
          }

          return (
            <div key={folderId} className="space-y-2">
              {/* 文件夹分割线 */}
              <FolderDivider
                folder={group.folder}
                projectCount={showCompletedProjects ? group.projects.length : group.incompleteCount}
                isExpanded={isExpanded}
                onFolderClick={scrollToFolder}
              />

              {/* 文件夹内的项目列表 */}
              {isExpanded && (
                <div className="divide-y divide-gray-200 dark:divide-ios-dark-gray4">
                  {/* 未完成项目 */}
                  {group.projects
                    .filter(project => project.stats.completionRate < 100)
                    .map((project) => (
                      <div key={project.id} className="py-0 first:pt-0 last:pb-0">
                        <ProjectCardV2
                          project={project}
                          mode="expanded"
                          onProjectClick={navigateToProjectDetail}
                          onTaskClick={handleTaskClick}
                          onTaskToggle={handleTaskToggle}
                          onTaskUpdate={handleTaskUpdate}
                          showQuickAddButton={true}
                          onQuickAddClick={handleQuickAddClick}
                          activeQuickAddProjectId={activeQuickAddProjectId}
                          onQuickAddClose={handleQuickAddClose}
                          onTaskCreated={handleTaskCreated}
                        />
                      </div>
                    ))}

                  {/* 已完成项目列表 */}
                  {showCompletedProjects && group.projects
                    .filter(project => project.stats.completionRate === 100)
                    .map((project) => (
                      <div key={project.id} className="py-0 first:pt-0 last:pb-0">
                        <ProjectCardV2
                          project={project}
                          mode="expanded"
                          onProjectClick={navigateToProjectDetail}
                          onTaskClick={handleTaskClick}
                          onTaskToggle={handleTaskToggle}
                          onTaskUpdate={handleTaskUpdate}
                          showQuickAddButton={true}
                          onQuickAddClick={handleQuickAddClick}
                          activeQuickAddProjectId={activeQuickAddProjectId}
                          onQuickAddClose={handleQuickAddClose}
                          onTaskCreated={handleTaskCreated}
                        />
                      </div>
                    ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }, [projectsWithStats, folders, expandedFolderIds, showCompletedProjects, setShowCompletedProjects, navigateToProjectDetail, handleTaskClick, handleTaskToggle, handleTaskUpdate, handleQuickAddClick, activeQuickAddProjectId, handleQuickAddClose, handleTaskCreated]);

  // 瀑布流分配函数
  const distributeProjectsToColumns = useCallback((projects: typeof projectsWithStats) => {
    // 计算项目权重
    const calculateProjectWeight = (project: typeof projects[0]) => {
      const baseWeight = 100; // 基础权重（项目标题、统计信息等）
      const taskWeight = Math.min(project.tasks.length * 25, 200); // 任务权重，最多200
      const descriptionWeight = project.context ? Math.min(project.context.length / 10, 100) : 0; // 描述权重
      const hasMoreTasksWeight = project.tasks.length > 6 ? 30 : 0; // 更多任务提示权重

      return baseWeight + taskWeight + descriptionWeight + hasMoreTasksWeight;
    };

    // 使用贪心算法分配到两列，保持高度平衡
    const leftColumn: typeof projects = [];
    const rightColumn: typeof projects = [];
    let leftWeight = 0;
    let rightWeight = 0;

    projects.forEach((project) => {
      const weight = calculateProjectWeight(project);

      // 将项目分配到当前权重较小的列
      if (leftWeight <= rightWeight) {
        leftColumn.push(project);
        leftWeight += weight;
      } else {
        rightColumn.push(project);
        rightWeight += weight;
      }
    });

    return { leftColumn, rightColumn };
  }, []);

  // 渲染卡片模式（瀑布流布局）
  const renderCardView = useMemo(() => {
    // 按文件夹分组项目
    const projectGroups = groupProjectsByFolder(projectsWithStats, folders);

    return (
      <div className="p-4 space-y-6">
        {projectGroups.map((group) => {
          const folderId = group.folder?.id || 'ungrouped';
          const isExpanded = group.folder
            ? (expandedFolderIds || []).includes(group.folder.id)
            : (expandedFolderIds || ['ungrouped']).includes('ungrouped');

          // 检查是否应该显示该文件夹组
          if (!shouldShowFolderGroup(group, showCompletedProjects)) {
            return null;
          }

          // 分离未完成和已完成项目
          const incompleteProjects = group.projects.filter(project => project.stats.completionRate < 100);
          const completedProjects = group.projects.filter(project => project.stats.completionRate >= 100);

          // 分配未完成项目到两列
          const incompleteColumns = incompleteProjects.length > 0
            ? distributeProjectsToColumns(incompleteProjects)
            : { leftColumn: [], rightColumn: [] };

          // 分配已完成项目到两列（如果需要显示）
          const completedColumns = showCompletedProjects && completedProjects.length > 0
            ? distributeProjectsToColumns(completedProjects)
            : { leftColumn: [], rightColumn: [] };

          return (
            <div key={folderId} className="space-y-4">
              {/* 文件夹分割线 */}
              <FolderDivider
                folder={group.folder}
                projectCount={showCompletedProjects ? group.projects.length : group.incompleteCount}
                isExpanded={isExpanded}
                onFolderClick={scrollToFolder}
              />

              {/* 文件夹内的项目瀑布流 */}
              {isExpanded && (
                <div className="space-y-6">
                  {/* 未完成项目瀑布流 */}
                  {incompleteProjects.length > 0 && (
                    <div className="grid grid-cols-2 gap-4">
                      {/* 左列 */}
                      <div className="space-y-4">
                        {incompleteColumns.leftColumn.map((project) => (
                          <ProjectCardV2
                            key={project.id}
                            project={project}
                            mode="card"
                            onProjectClick={navigateToProjectDetail}
                            onTaskClick={handleTaskClick}
                            onTaskToggle={handleTaskToggle}
                            onTaskUpdate={handleTaskUpdate}
                          />
                        ))}
                      </div>

                      {/* 右列 */}
                      <div className="space-y-4">
                        {incompleteColumns.rightColumn.map((project) => (
                          <ProjectCardV2
                            key={project.id}
                            project={project}
                            mode="card"
                            onProjectClick={navigateToProjectDetail}
                            onTaskClick={handleTaskClick}
                            onTaskToggle={handleTaskToggle}
                            onTaskUpdate={handleTaskUpdate}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 已完成项目瀑布流 */}
                  {showCompletedProjects && completedProjects.length > 0 && (
                    <div className="grid grid-cols-2 gap-4">
                      {/* 左列 */}
                      <div className="space-y-4">
                        {completedColumns.leftColumn.map((project) => (
                          <ProjectCardV2
                            key={project.id}
                            project={project}
                            mode="card"
                            onProjectClick={navigateToProjectDetail}
                            onTaskClick={handleTaskClick}
                            onTaskToggle={handleTaskToggle}
                            onTaskUpdate={handleTaskUpdate}
                          />
                        ))}
                      </div>

                      {/* 右列 */}
                      <div className="space-y-4">
                        {completedColumns.rightColumn.map((project) => (
                          <ProjectCardV2
                            key={project.id}
                            project={project}
                            mode="card"
                            onProjectClick={navigateToProjectDetail}
                            onTaskClick={handleTaskClick}
                            onTaskToggle={handleTaskToggle}
                            onTaskUpdate={handleTaskUpdate}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }, [projectsWithStats, folders, expandedFolderIds, showCompletedProjects, distributeProjectsToColumns, navigateToProjectDetail, handleTaskClick, handleTaskToggle, handleTaskUpdate]);



  // 渲染标签视图
  const renderTabsView = useMemo(() => {
    return (
      <ProjectTabsView
        projects={projectsWithStats}
        projectsGrouped={projectsGrouped}
        showCompletedProjects={showCompletedProjects}
        onShowCompletedProjectsChange={setShowCompletedProjects}
        onTaskToggle={handleTaskToggle}
        onTaskClick={handleTaskClick}
        onTaskUpdate={handleTaskUpdate}
      />
    );
  }, [projectsWithStats, projectsGrouped, showCompletedProjects, setShowCompletedProjects, handleTaskToggle, handleTaskClick, handleTaskUpdate]);

  // 获取当前内容
  const getCurrentContent = () => {
    switch (projectViewMode) {
      case 'compact':
        return renderCompactView;
      case 'expanded':
        return renderExpandedView;
      case 'card':
        return renderCardView;
      case 'tabs':
        return renderTabsView;
      default:
        return renderCompactView;
    }
  };

  return (
    <TaskDndContext
      onCreateTask={() => {}} // 项目页面不需要创建任务
      onUpdateTask={handleTaskUpdate}
    >
      <div className={`
        flex flex-col h-screen
        ${isDark ? 'bg-ios-dark-gray6' : 'bg-gray-50'}
      `}>
        {/* 头部 */}
        <ProjectV2Header />

        {/* 内容区域 - 添加顶部间距以避让固定的 header 和底部避让 */}
        <div className={`
          flex-1 overflow-auto pt-[calc(4rem+env(safe-area-inset-top))]
          ${layoutMode === 'desktop' ? 'pb-4' : 'pb-32'}
        `}>
          {getCurrentContent()}
        </div>

        {/* 底部操作区域 */}
        {layoutMode === 'desktop' ? (
          /* 桌面模式：使用FAB */
          <DraggableTaskButton onClick={handleCreateProject} />
        ) : (
          /* 移动模式：使用ChatInputContainer的quickbarMode */
          <ChatInputContainer
            quickbarMode={true}
            onNewTask={handleCreateTask}
            onNewProject={handleCreateProject}
            onFocusNavigateToChat={handleNavigateToChatAndFocus}
            onFileUploadNavigateToChat={handleNavigateToChatAndUpload}
            onVoiceNavigateToChat={handleNavigateToChatAndVoice}
          />
        )}

        {/* 任务详情弹窗 */}
        <TaskDetailSheet
          taskId={selectedTaskId}
          isOpen={isTaskDetailOpen}
          onClose={handleCloseTaskDetail}
        />

        {/* 项目编辑/创建模态框 */}
        {editingProject && (
          <ProjectEditModal
            isOpen={isEditModalOpen}
            onClose={handleCloseEditModal}
            onSave={handleSaveProject}
            projectId={editingProject.id}
            initialTitle={editingProject.title}
            initialDescription={editingProject.description}
            initialFolderId={editingProject.folderId}
            isCreating={!editingProject.id}
          />
        )}
      </div>
    </TaskDndContext>
  );
};
